"use client";

import { m, useInView } from "framer-motion";
import { useRef } from "react";
import Image from "next/image";
import SectionHeader from "../sectionHeader";

interface ArrowItem {
  title: string;
  text: string;
}

interface Props {
  data?: {
    title?: string;
    headline?: string;
    text?: string;
    subtitle?: string;
    items: ArrowItem[];
  };
}

const itemVariants = {
  initial: { opacity: 0, y: 32 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      type: "spring",
    },
  },
};

function Arrow({ direction }: { direction: "down" | "right" }) {
  return (
    <Image
      src="/images/arrow-red.svg"
      alt="Directional arrow"
      aria-hidden="true"
      width={24}
      height={30}
      className={`shrink-0 w-10 h-auto ${direction === "down" ? "rotate-180" : "rotate-90"}`}
    />
  );
}

export default function ArrowsSection({ data }: Props) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <m.section
      ref={ref}
      className="section"
      transition={{ staggerChildren: 0.1, delayChildren: 0.2 }}
      initial="initial"
      animate={isInView ? "animate" : "initial"}
    >
      <div className="container mx-auto flex flex-col gap-6">
        <SectionHeader title={data?.title} headline={data?.headline} text={data?.text} />
        {data?.subtitle && (
          <p className="font-sans uppercase text-lg lg:text-xl">{data.subtitle}</p>
        )}
        {/* Mobile: vertical */}
        <div className="lg:hidden">
        <div className="flex flex-col items-center gap-6 w-full">
          <Arrow direction="down" />
          {data?.items?.map((item, index) => (
            <m.div key={index} variants={itemVariants} className="flex flex-col items-center gap-6">
              <div className="flex flex-col gap-3 text-center">
                <span className="font-display uppercase text-xl">{item.title}</span>
                <span className="font-mono uppercase text-xs leading-relaxed">{item.text}</span>
              </div>
              <Arrow direction="down" />
            </m.div>
          ))}
        </div>
        </div>

        {/* Desktop: horizontal */}
        <div className="hidden lg:flex items-center justify-evenly gap-6 w-full">
          <Arrow direction="right" />
          {data?.items?.map((item, index) => (
            <m.div key={index} variants={itemVariants} className="contents">
              <div className="flex flex-col gap-3 max-w-[200px]">
                <span className="font-display uppercase text-xl lg:text-2xl">{item.title}</span>
                <span className="font-mono uppercase text-xs leading-relaxed">{item.text}</span>
              </div>
              <Arrow direction="right" />
            </m.div>
          ))}
        </div>
      </div>
    </m.section>
  );
}
